import { useEffect, useRef, useState } from 'react';
import parseUserInputAddress, { formatParsedAddress } from './parseUserInputAddress';
import { getCoordinates } from './geocode';

// Resolves the delivery coordinates for an Order. Preference order:
//   1. `order.property.address.{lat,lng}` — already hydrated for most orders.
//   2. Structured `userInputAddress` → forward-geocode once and cache.
//
// Falls back to `null` when neither source is available.

function toNum(v) {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

export function getPropertyCoord(order) {
  const addr = order?.property?.address;
  if (!addr) return null;
  const lat = toNum(addr.lat);
  const lng = toNum(addr.lng);
  if (lat == null || lng == null) return null;
  return { lat, lng };
}

export function getUserInputAddressString(order) {
  const { parsed, raw } = parseUserInputAddress(order?.userInputAddress);
  if (parsed) {
    const summary = formatParsedAddress(parsed);
    if (summary) return summary;
  }
  if (typeof raw === 'string' && raw.trim().length > 3) return raw.trim();
  return '';
}

// React hook: given a list of orders, progressively fills a { [orderId]: coord }
// map. Property coords are resolved synchronously. User-input-address coords
// are geocoded lazily (max `concurrency` at a time) and cached in a ref so we
// don't burn quota on repeat renders.
export function useResolvedOrderCoords(orders, { concurrency = 3 } = {}) {
  const cacheRef = useRef(new Map());
  const [coords, setCoords] = useState({});

  useEffect(() => {
    let cancelled = false;

    const synchronous = {};
    const needsGeo = [];
    (orders || []).forEach((order) => {
      if (!order?._id) return;
      const propertyCoord = getPropertyCoord(order);
      if (propertyCoord) {
        cacheRef.current.set(order._id, propertyCoord);
        synchronous[order._id] = propertyCoord;
        return;
      }
      if (cacheRef.current.has(order._id)) {
        const cached = cacheRef.current.get(order._id);
        if (cached) synchronous[order._id] = cached;
        return;
      }
      const addr = getUserInputAddressString(order);
      if (!addr) return;
      needsGeo.push({ id: order._id, addr });
    });

    setCoords((prev) => ({ ...prev, ...synchronous }));

    if (needsGeo.length === 0) return undefined;

    const queue = needsGeo.slice();
    let active = 0;

    const runNext = async () => {
      if (cancelled) return;
      if (queue.length === 0 && active === 0) return;
      while (active < concurrency && queue.length > 0) {
        const item = queue.shift();
        active += 1;
        (async () => {
          try {
            const coord = await getCoordinates(item.addr);
            if (cancelled) return;
            cacheRef.current.set(item.id, coord || null);
            if (coord) {
              setCoords((prev) => ({ ...prev, [item.id]: coord }));
            }
          } catch (err) {
            cacheRef.current.set(item.id, null);
          } finally {
            active -= 1;
            runNext();
          }
        })();
      }
    };

    runNext();

    return () => {
      cancelled = true;
    };
  }, [orders, concurrency]);

  return coords;
}
